import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { analytics } from '@/services/analytics';
import { inputMethodFromEvent } from '@/services/analyticsContext';
import { billingService, TransactionSubmitError } from '@/services/billingService';
import { authService, type Entitlements } from '@/services/authService';
import {
  APPLE_PRODUCT_IDS,
  Purchases,
  type StoreProduct,
  type StoreTransaction,
} from '@/native/purchases';
import PlanCard, { planButtonClasses, planStatusClasses } from './PlanCard';

/**
 * The native app's pricing surface: the four App Store subscriptions, bought
 * in place.
 *
 * Prices come from StoreKit, never from /api/pricing. The pricing endpoint
 * knows GBP, EUR and USD and routes by the caller's country; Apple charges in
 * the customer's storefront currency, which outside those three is a price we
 * did not set by hand. Showing one number and charging another is both a bad
 * experience and a rejection, so every figure here is Apple's own
 * `displayPrice`, and the period is read off the product rather than assumed.
 *
 * Everything Apple requires adjacent to the purchase is on this surface: the
 * subscription's name, its length, its price, what it includes, that it
 * renews automatically, links to the terms and the privacy policy, a Restore
 * Purchases action, and the support contact for questions and refunds.
 *
 * Native only — the web keeps PricingTiers and has no checkout yet.
 */

type Period = 'monthly' | 'annual';

interface TierCopy {
  key: 'member' | 'scholar';
  name: string;
  tagline: string;
  features: string[];
  products: Record<Period, string>;
}

// The free tier, shown beside the paid ones so its allowance is visible and
// a subscriber can see the way back to it. It is not an App Store product:
// nothing to buy, nothing to switch to — returning to it is cancelling the
// subscription, which Apple's sheet does and which runs to the period end.
// The "per month" wording is the web's and the modal's, deliberately.
const FREE_TIER = {
  name: 'Free',
  tagline: 'For getting started',
  features: ['Access to all books', '30 minutes of conversation with Aleph per month'],
};

// The same words the pricing page uses, minus anything about price. What
// Scholar buys over Member is conversation minutes and nothing else; the
// catalog is open on every tier, free included.
const TIERS: TierCopy[] = [
  {
    key: 'member',
    name: 'Member',
    tagline: 'For regular readers',
    features: [
      'Access to all books',
      'Unlimited audiobook listening',
      '3 hours of conversation with Aleph per month',
    ],
    products: {
      monthly: APPLE_PRODUCT_IDS.memberMonthly,
      annual: APPLE_PRODUCT_IDS.memberAnnual,
    },
  },
  {
    key: 'scholar',
    name: 'Scholar',
    tagline: 'For the deeply curious',
    features: [
      'Access to all books',
      'Unlimited audiobook listening',
      'Unlimited conversation with Aleph',
    ],
    products: {
      monthly: APPLE_PRODUCT_IDS.scholarMonthly,
      annual: APPLE_PRODUCT_IDS.scholarAnnual,
    },
  },
];

const SUPPORT_EMAIL = 'say@hi-aleph.com';

/** "/ month" or "/ year", from the product's own period. */
const periodLabel = (product: StoreProduct | undefined, fallback: Period): string => {
  const unit = product?.period?.unit;
  const value = product?.period?.value ?? 1;
  if (unit === 'month' && value === 1) return '/ month';
  if (unit === 'year' && value === 1) return '/ year';
  if (unit && unit !== 'unknown') return `/ ${value} ${unit}${value === 1 ? '' : 's'}`;
  return fallback === 'annual' ? '/ year' : '/ month';
};

/** A renewal instant for a sentence. Sandbox renews in minutes, so the time
 * is shown too; in production it is a date a month or a year out. */
const formatWhen = (milliseconds: number | undefined): string =>
  milliseconds === undefined
    ? 'your next renewal'
    : new Date(milliseconds).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const tierOf = (productId: string | undefined): TierCopy | undefined =>
  TIERS.find((t) => t.products.monthly === productId || t.products.annual === productId);

const periodOf = (productId: string | undefined): Period | undefined =>
  TIERS.flatMap((t) => (Object.keys(t.products) as Period[]).map((p) => [t.products[p], p] as const)).find(
    ([id]) => id === productId,
  )?.[1];

const planLabel = (productId: string | undefined): string => {
  const tier = tierOf(productId);
  const period = periodOf(productId);
  return tier ? `${tier.name}${period ? ` (${period})` : ''}` : 'a plan';
};

// The server stores `premium` for what the store calls Scholar.
const STORED_TIER_NAMES: Record<string, string> = { free: 'Free', member: 'Member', premium: 'Scholar' };

/**
 * One sentence, above everything else, saying what the person has and what
 * is happening to it. StoreKit knows the live subscription and its renewal
 * preference; the server knows a subscription that has already ended, which
 * StoreKit's current entitlements no longer mention at all.
 */
const summarise = (active: StoreTransaction[], account: Entitlements | null): string | null => {
  const current = active[0];
  if (current) {
    const when = formatWhen(current.expirationDate);
    // Whether it will renew is answered by either source. StoreKit's local
    // renewal info can lag a cancellation made moments ago in its own sheet;
    // the server hears it from Apple within seconds. Either saying "no" wins.
    const willEnd =
      current.willAutoRenew === false || account?.status === 'canceled' || account?.status === 'expired';
    const fate =
      current.autoRenewProductId && current.autoRenewProductId !== current.productId
        ? `switches to ${planLabel(current.autoRenewProductId)} on ${when}`
        : willEnd
          ? `ends on ${when}`
          : account?.status === 'past_due'
            ? `your last payment didn't go through; access continues until ${when} while the App Store retries`
            : `renews on ${when}`;
    return `You're on ${planLabel(current.productId)} — ${fate}.`;
  }
  if (!account) return null;
  if (account.status === 'expired' && account.stored_tier !== 'free') {
    const ended = account.current_period_end
      ? ` on ${new Date(account.current_period_end).toLocaleDateString(undefined, { dateStyle: 'medium' })}`
      : '';
    return `Your ${STORED_TIER_NAMES[account.stored_tier] ?? account.stored_tier} subscription ended${ended}. Subscribe again to continue.`;
  }
  if (account.tier !== 'free') {
    // Granted by the server with nothing in this device's StoreKit: comped by
    // hand, bought on the web, or bought under a different Apple ID. Still
    // theirs — the server is what gates usage — so say so, and say where it
    // comes from so nobody buys it twice.
    const origin =
      account.provider === 'apple'
        ? 'Your subscription was bought with a different Apple ID.'
        : account.provider === 'stripe'
          ? 'Manage it on hi-aleph.com.'
          : "It's included with your account.";
    return `You're on ${account.tier_name}. ${origin}`;
  }
  return "You're on the free plan.";
};

// A tier the server grants when StoreKit on this device holds nothing, as
// the card key it corresponds to. Null when StoreKit has something — then
// StoreKit's product is the truth about which card is current.
const grantedTierKey = (active: StoreTransaction[], account: Entitlements | null): TierCopy['key'] | null => {
  if (active.length > 0 || !account) return null;
  if (account.tier === 'premium') return 'scholar';
  if (account.tier === 'member') return 'member';
  return null;
};
const TIER_RANK: Record<TierCopy['key'], number> = { member: 1, scholar: 2 };

const friendlyError = (error: unknown): string => {
  if (error instanceof TransactionSubmitError) {
    switch (error.kind) {
      case 'wrong_account':
        return 'This subscription belongs to a different Aleph account. Sign in to that account to use it.';
      case 'rejected':
        return `Your purchase went through but could not be recorded. Please contact ${SUPPORT_EMAIL}.`;
      case 'unavailable':
        return "Your purchase went through but we couldn't reach Aleph. It will be applied the next time you open the app.";
    }
  }
  // StoreKit's own words are the useful part of an App Store failure
  // ("Cannot connect to the App Store", "Payment not allowed"); keep them.
  const detail = error instanceof Error && error.message ? ` (${error.message.replace(/^Purchase failed: /, '')})` : '';
  return `Something went wrong with the App Store${detail}. Please try again.`;
};

interface AppStorePlansProps {
  // 'modal' is the compact layout inside the usage-limit modal; 'page' the
  // roomier standalone layout.
  variant?: 'modal' | 'page';
  // Where the user opened plans from — sent with analytics events.
  source?: string;
  /** After a purchase the server has recorded. The modal uses it to close. */
  onPurchased?: () => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
}

const AppStorePlans: React.FC<AppStorePlansProps> = ({
  variant = 'page',
  source,
  onPurchased,
  onNavigateToTerms,
  onNavigateToPrivacy,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const [products, setProducts] = useState<Record<string, StoreProduct> | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [period, setPeriod] = useState<Period>('monthly');
  // What StoreKit says is active on this Apple ID, with each one's renewal
  // preference. The server's effective tier is the gate for usage; this is
  // for the cards, which need the product rather than the tier — and need to
  // know that a downgrade or a change of period is already scheduled, since
  // StoreKit refuses to "buy" a product that is already the pending renewal.
  const [active, setActive] = useState<StoreTransaction[]>([]);
  // The server's view of the account, for what StoreKit no longer shows: a
  // subscription that has ended. Null until it answers; the line is omitted.
  const [account, setAccount] = useState<Entitlements | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const refreshTimers = useRef<number[]>([]);
  useEffect(() => () => refreshTimers.current.forEach((id) => window.clearTimeout(id)), []);
  // A switch the person just confirmed in Apple's sheet, applied to the
  // cards at once. StoreKit's own renewal info takes up to a minute to say
  // so, and the server records only that a preference changed, not to what;
  // but a purchase() that hands back the current transaction instead of a
  // new one is exactly a deferred switch to the product that was tapped.
  // Dropped as soon as StoreKit agrees, or the transaction it was for is
  // gone (the renewal that lands the switch).
  const [pendingSwitch, setPendingSwitch] = useState<{ fromTransactionId: string; toProductId: string } | null>(null);


  const surface = variant === 'modal' ? 'usage_limit_modal' : 'plans_page';

  const refreshActive = useCallback(async () => {
    try {
      const { transactions } = await Purchases.getCurrentEntitlements();
      setActive(transactions);
    } catch (error) {
      console.warn('Could not read current entitlements:', error);
    }
    try {
      setAccount(await authService.getEntitlements());
    } catch (error) {
      console.warn('Could not read the account entitlements:', error);
    }
  }, []);

  const lookAgain = useCallback(
    (delays: number[]) => {
      for (const delay of delays) {
        refreshTimers.current.push(window.setTimeout(() => void refreshActive(), delay));
      }
    },
    [refreshActive],
  );

  useEffect(() => {
    let cancelled = false;
    billingService
      .products()
      .then((list) => {
        if (cancelled) return;
        setProducts(Object.fromEntries(list.map((p) => [p.id, p])));
        if (list.length === 0) setLoadError(true);
      })
      .catch((error) => {
        console.warn('Could not load App Store products:', error);
        analytics.trackErrorShown('app_store_products', error, { surface });
        if (!cancelled) setLoadError(true);
      });
    void refreshActive();
    return () => {
      cancelled = true;
    };
  }, [refreshActive, surface]);

  // Anything recorded from any path — an out-of-band renewal, a restore from
  // elsewhere — updates which card is current.
  useEffect(() => billingService.onRecorded(() => void refreshActive()), [refreshActive]);

  useEffect(() => {
    if (!pendingSwitch) return;
    const from = active.find((t) => t.id === pendingSwitch.fromTransactionId);
    const storeKitAgrees = from?.autoRenewProductId === pendingSwitch.toProductId;
    if (!from || storeKitAgrees) setPendingSwitch(null);
  }, [active, pendingSwitch]);

  const handleBuy = async (event: React.MouseEvent, tier: TierCopy, productId: string) => {
    const product = products?.[productId];
    analytics.track('App Store Purchase Started', {
      product_id: productId,
      tier: tier.key,
      period,
      display_price: product?.displayPrice,
      currency: product?.currencyCode,
      source,
      surface,
      trigger: 'button',
      input_method: inputMethodFromEvent(event),
    });
    setBusy(productId);
    setNotice(null);
    try {
      const result = await billingService.purchase(productId);
      analytics.track('App Store Purchase Finished', {
        product_id: productId,
        tier: tier.key,
        period,
        outcome: result.outcome,
        applied: result.outcome === 'purchased' ? result.receipt.applied : undefined,
        source,
        surface,
      });
      if (result.outcome === 'purchased') {
        await refreshActive();
        const { receipt, transaction } = result;
        // StoreKit's `.success` is not the same as "something was bought".
        // It also hands back the transaction it already holds when it
        // believes the product is already owned: the current one for a
        // deferred downgrade, or — seen on the device — a stale, expired one
        // from a cache that has not caught up, with no sheet shown at all.
        // The server's receipt is what says which of these happened.
        if (transaction.productId !== productId) {
          // A downgrade or a change of period: the plan they have stays, and
          // the one they chose starts at its renewal.
          setPendingSwitch({ fromTransactionId: transaction.id, toProductId: productId });
          lookAgain([4000, 15000, 45000]);
          setNotice(
            `Your plan will switch to ${tier.name} on ${formatWhen(transaction.expirationDate)}. Until then nothing changes.`,
          );
          onPurchased?.();
        } else if (receipt.applied) {
          lookAgain([4000, 15000]);
          setNotice(`You're now a ${tier.name}. Thank you.`);
          onPurchased?.();
        } else if (receipt.reason === 'replay') {
          setNotice(`You're already on ${tier.name}.`);
          onPurchased?.();
        } else {
          // 'expired': the App Store returned a subscription that has run
          // out. Nothing was bought and nothing was charged.
          setNotice(
            'The App Store returned your previous subscription, which has ended, so nothing was bought. Please try again in a moment.',
          );
        }
      } else if (result.outcome === 'pending') {
        setNotice('Your purchase is waiting for approval. It will be applied once approved.');
      } else {
        // Cancelled — by the person, or by the App Store abandoning the sheet
        // (the app going to the background mid-purchase does it). Say so,
        // or a purchase that silently failed looks the same as one that
        // was never attempted.
        setNotice('No purchase was made.');
      }
    } catch (error) {
      analytics.trackErrorShown('app_store_purchase', error, { product_id: productId, surface });
      setNotice(friendlyError(error));
    } finally {
      setBusy(null);
    }
  };

  // The system sheet, in-app: change or cancel, then come back here. A
  // cancellation or change made there arrives through Transaction.updates
  // and the server's notification; the current card follows via onRecorded.
  const handleManage = async () => {
    setBusy('manage');
    try {
      await billingService.manageSubscriptions();
      await refreshActive();
    } finally {
      setBusy(null);
    }
    // What was done in the sheet reaches us two ways, neither instant:
    // StoreKit's local status catches up on its own clock, and Apple's
    // notification reaches the server a few seconds later. Look again.
    lookAgain([4000, 12000, 45000]);
  };

  const handleRestore = async (event: React.MouseEvent) => {
    analytics.track('App Store Restore Started', {
      source,
      surface,
      trigger: 'button',
      input_method: inputMethodFromEvent(event),
    });
    setBusy('restore');
    setNotice(null);
    try {
      const batch = await billingService.restore();
      await refreshActive();
      const wrongAccount = batch.rejected.find(
        (r) => r.error instanceof TransactionSubmitError && r.error.kind === 'wrong_account',
      );
      if (batch.recorded.length > 0) {
        setNotice('Your purchases have been restored.');
        onPurchased?.();
      } else if (wrongAccount) {
        setNotice(friendlyError(wrongAccount.error));
      } else {
        setNotice('No purchases to restore on this Apple ID.');
      }
    } catch (error) {
      analytics.trackErrorShown('app_store_restore', error, { surface });
      setNotice(friendlyError(error));
    } finally {
      setBusy(null);
    }
  };

  const mutedText = isDarkMode ? 'text-gray-300' : 'text-gray-600';
  const linkClasses = 'underline underline-offset-2 hover:text-blue-500 cursor-pointer';
  // What StoreKit says, with a switch confirmed moments ago laid over it
  // until StoreKit says it too.
  const shown: StoreTransaction[] = active.map((t) =>
    pendingSwitch && t.id === pendingSwitch.fromTransactionId && t.autoRenewProductId === undefined
      ? { ...t, autoRenewProductId: pendingSwitch.toProductId }
      : pendingSwitch && t.id === pendingSwitch.fromTransactionId && t.autoRenewProductId === t.productId
        ? { ...t, autoRenewProductId: pendingSwitch.toProductId }
        : t,
  );
  const subscribed = shown.length > 0;
  const activeProductIds = new Set(shown.map((t) => t.productId));
  // Product → the active transaction that will renew as it (a scheduled switch).
  const pendingFrom = new Map<string, StoreTransaction>();
  for (const t of shown) {
    if (t.autoRenewProductId && t.autoRenewProductId !== t.productId) {
      pendingFrom.set(t.autoRenewProductId, t);
    }
  }

  if (loadError) {
    return (
      <p className={`text-sm ${mutedText}`}>
        Couldn't load plans from the App Store right now. Please try again later.
      </p>
    );
  }

  if (!products) {
    return <p className={`text-sm ${mutedText}`}>Loading plans…</p>;
  }

  const summary = summarise(shown, account);
  const granted = grantedTierKey(shown, account);
  // Already set not to renew (by StoreKit's word or the server's): Free
  // arrives on its own at the period end, so the Free card says when.
  const ending = shown[0];
  const freeReturnsAt =
    ending &&
    (ending.willAutoRenew === false || account?.status === 'canceled') &&
    !(ending.autoRenewProductId && ending.autoRenewProductId !== ending.productId)
      ? formatWhen(ending.expirationDate)
      : null;

  return (
    <div>
      {summary && (
        <p data-testid="plan-summary" className={`mb-4 text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
          {summary}
        </p>
      )}

      {/* Monthly / annual */}
      <div
        role="radiogroup"
        aria-label="Billing period"
        className={`inline-flex rounded-full border p-1 mb-5 ${
          isDarkMode ? 'border-gray-600' : 'border-gray-300'
        }`}
      >
        {(['monthly', 'annual'] as Period[]).map((option) => (
          <button
            key={option}
            role="radio"
            aria-checked={period === option}
            onClick={() => setPeriod(option)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer ${
              period === option
                ? 'bg-blue-600 text-white'
                : isDarkMode
                  ? 'text-gray-300'
                  : 'text-gray-600'
            }`}
          >
            {option === 'monthly' ? 'Monthly' : 'Annual'}
          </button>
        ))}
      </div>

      <div
        className={`grid grid-cols-1 ${
          variant === 'page' ? 'md:grid-cols-3 gap-6 lg:gap-8' : 'md:grid-cols-3 gap-4'
        }`}
      >
        <PlanCard
          name={FREE_TIER.name}
          tagline={FREE_TIER.tagline}
          price="Free"
          priceNote="No subscription. Always here to come back to."
          features={FREE_TIER.features}
          // Outline Free only once the server has said it is theirs.
          highlighted={account !== null && !subscribed && !granted}
          variant={variant}
          isDarkMode={isDarkMode}
        >
          {subscribed ? (
            <div className={`text-center text-xs ${mutedText}`}>
              <p className="mb-2">
                {freeReturnsAt
                  ? `Your plan returns to Free on ${freeReturnsAt}.`
                  : 'To return to Free, cancel your subscription in the App Store. You keep your plan until the end of the period you have paid for.'}
              </p>
              {!freeReturnsAt && (
                <button
                  type="button"
                  onClick={() => void handleManage()}
                  disabled={busy !== null}
                  className={linkClasses}
                >
                  {busy === 'manage' ? 'Opening…' : 'Cancel in the App Store'}
                </button>
              )}
            </div>
          ) : granted ? (
            <div className={planStatusClasses(isDarkMode)}>Included in your plan</div>
          ) : (
            <div className={planStatusClasses(isDarkMode, 'accent')}>Your current plan</div>
          )}
        </PlanCard>

        {TIERS.map((tier) => {
          const productId = tier.products[period];
          const product = products[productId];
          const current = shown.find((t) => t.productId === productId);
          const scheduledFrom = pendingFrom.get(productId);
          // The server grants this tier and StoreKit holds nothing: they have
          // it (both periods, since a grant has no period), and anything
          // below it is already included.
          const isGranted = granted === tier.key;
          const isIncluded = granted !== null && TIER_RANK[tier.key] < TIER_RANK[granted];
          const isBusy = busy === productId;
          // What becomes of the current plan at its renewal, if not simply more of it.
          const currentFate = !current
            ? null
            : current.autoRenewProductId && current.autoRenewProductId !== current.productId
              ? `until ${formatWhen(current.expirationDate)}`
              : current.willAutoRenew === false
                ? `ends ${formatWhen(current.expirationDate)}`
                : null;

          return (
            <PlanCard
              key={tier.key}
              name={tier.name}
              tagline={tier.tagline}
              price={product?.displayPrice ?? '—'}
              priceSuffix={periodLabel(product, period)}
              priceNote={`${period === 'annual' ? 'Billed yearly.' : 'Billed monthly.'} Renews automatically until cancelled.`}
              features={tier.features}
              highlighted={Boolean(current) || isGranted}
              variant={variant}
              isDarkMode={isDarkMode}
            >
              {current || isGranted ? (
                <div className={planStatusClasses(isDarkMode, 'accent')}>
                  Your current plan{currentFate ? ` · ${currentFate}` : ''}
                </div>
              ) : isIncluded ? (
                <div className={planStatusClasses(isDarkMode)}>Included in your plan</div>
              ) : scheduledFrom ? (
                <div className={planStatusClasses(isDarkMode)}>
                  Switching to this plan on {formatWhen(scheduledFrom.expirationDate)}
                </div>
              ) : (
                <button
                  onClick={(event) => void handleBuy(event, tier, productId)}
                  disabled={busy !== null || !product}
                  className={planButtonClasses}
                >
                  {isBusy
                    ? 'Opening the App Store…'
                    : !product
                      ? 'Not available right now'
                      : subscribed
                        ? 'Switch to this plan'
                        : 'Subscribe'}
                </button>
              )}
            </PlanCard>
          );
        })}
      </div>

      {notice && (
        <p role="status" className="mt-4 text-sm font-medium text-blue-500">
          {notice}
        </p>
      )}

      {/* What Apple requires beside the purchase, and the way back in. */}
      <div className={`mt-6 space-y-2 text-xs ${mutedText}`}>
        <p>
          Payment is charged to your Apple ID account when you confirm the purchase. Your
          subscription renews automatically unless you cancel at least 24 hours before the end of
          the current period. You can manage or cancel it in your App Store account settings, and
          a change of plan takes effect as described by the App Store when you confirm it.
        </p>
        <p className="flex flex-wrap gap-x-4 gap-y-1">
          <button type="button" onClick={(event) => void handleRestore(event)} disabled={busy !== null} className={linkClasses}>
            {busy === 'restore' ? 'Restoring…' : 'Restore Purchases'}
          </button>
          {subscribed && (
            <button
              type="button"
              onClick={() => void handleManage()}
              disabled={busy !== null}
              className={linkClasses}
            >
              {busy === 'manage' ? 'Opening…' : 'Manage subscription'}
            </button>
          )}
          {onNavigateToTerms ? (
            <button type="button" onClick={onNavigateToTerms} className={linkClasses}>
              Terms of Use
            </button>
          ) : (
            <a href="https://hi-aleph.com/terms" target="_blank" rel="noopener noreferrer" className={linkClasses}>
              Terms of Use
            </a>
          )}
          {onNavigateToPrivacy ? (
            <button type="button" onClick={onNavigateToPrivacy} className={linkClasses}>
              Privacy Policy
            </button>
          ) : (
            <a href="https://hi-aleph.com/privacy" target="_blank" rel="noopener noreferrer" className={linkClasses}>
              Privacy Policy
            </a>
          )}
        </p>
        <p>
          Questions or refunds: <a href={`mailto:${SUPPORT_EMAIL}`} className={linkClasses}>{SUPPORT_EMAIL}</a>
        </p>
      </div>
    </div>
  );
};

export default AppStorePlans;
